import * as React from "react"
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { Bell, Calendar, ChevronDown, Pencil } from "lucide-react"
import { useTranslation } from "react-i18next"
import { format, isToday, differenceInDays, formatDistanceToNow } from "date-fns"
import { es } from 'date-fns/locale'
import { cn, getStatusColor, getPriorityColor } from "@/lib/utils"
import type { Reminder } from "@/types/reminders"
import { Badge } from "@/components/ui/badge"
import { AppleButton } from "@/components/ui/apple-button"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { AssetRecordForm } from "@/components/forms/AssetRecordForm"
import { toast } from "@/components/ui/use-toast"
import { Link } from "react-router-dom"

interface MiniRemindersProps {
  onReminderChange?: () => void;
}

export function MiniReminders({ onReminderChange }: MiniRemindersProps) {
  const { t, i18n } = useTranslation()
  const pageSize = 15 // Show 15 reminders instead of 5
  const [showCompleteDialog, setShowCompleteDialog] = useState(false)
  const [selectedReminder, setSelectedReminder] = useState(null)

  // Get the current locale for date-fns
  const dateLocale = i18n.language.startsWith('es') ? es : undefined

  const { data: remindersData, refetch: refetchReminders } = useQuery<{
    reminders: Reminder[];
  }>({
    queryKey: ['miniReminders'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('reminders')
        .select(`
          id,
          title,
          description,
          recurrency_value,
          recurrency_unit,
          priority,
          status,
          next_trigger_at,
          last_triggered_at,
          created_at,
          updated_at,
          onetime,
          assets:asset_id (
            id,
            name,
            asset_name,
            place:place_id (
              id,
              name,
              description
            ),
            company:company_id (
              id,
              name,
              description
            )
          )
        `)
        .eq('user_id', user.id)
        .order('next_trigger_at', { ascending: true })
        .limit(pageSize)

      if (error) throw error

      return {
        reminders: data as unknown as Reminder[],
      }
    },
  })

  const handleCompleteClick = (reminder) => {
    setSelectedReminder(reminder)
    setShowCompleteDialog(true)
  }

  const handleCompleteSuccess = async () => {
    if (!selectedReminder) return

    try {
      if (selectedReminder.onetime) {
        // For one-time reminders, delete after completion
        const { error } = await supabase
          .from('reminders')
          .delete()
          .eq('id', selectedReminder.id)

        if (error) throw error
      } else {
        // For recurring reminders, calculate next trigger date
        const nextTriggerDate = new Date()
        switch (selectedReminder.recurrency_unit) {
          case 'days':
            nextTriggerDate.setDate(nextTriggerDate.getDate() + selectedReminder.recurrency_value)
            break
          case 'weeks':
            nextTriggerDate.setDate(nextTriggerDate.getDate() + (selectedReminder.recurrency_value * 7))
            break
          case 'months':
            nextTriggerDate.setMonth(nextTriggerDate.getMonth() + selectedReminder.recurrency_value)
            break
          case 'years':
            nextTriggerDate.setFullYear(nextTriggerDate.getFullYear() + selectedReminder.recurrency_value)
            break
        }

        // Update the reminder with new next_trigger_at and last_triggered_at
        const { error } = await supabase
          .from('reminders')
          .update({
            next_trigger_at: nextTriggerDate.toISOString(),
            last_triggered_at: new Date().toISOString()
          })
          .eq('id', selectedReminder.id)

        if (error) throw error
      }

      setShowCompleteDialog(false)
      setSelectedReminder(null)
      refetchReminders()
      onReminderChange?.()
    } catch (error) {
      console.error('Error updating reminder:', error)
      toast({
        title: t('common.error'),
        description: t('reminders.updateError'),
        variant: 'destructive',
      })
    }
  }

  const getDueStatusColor = (nextTriggerAt: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time part for accurate date comparison
    const triggerDate = new Date(nextTriggerAt);
    triggerDate.setHours(0, 0, 0, 0); // Reset time part for accurate date comparison
    const daysDiff = differenceInDays(triggerDate, today);

    if (isToday(triggerDate)) {
      return "bg-green-50 border-green-200"; // Today's date - green
    } else if (daysDiff <= -2) {
      return "bg-red-50 border-red-200"; // More than 2 days overdue - red
    } else if (daysDiff === -1) {
      return "bg-yellow-50 border-yellow-200"; // 1 day overdue - yellow
    }
    return "bg-white"; // Future date - white
  };

  if (!remindersData?.reminders.length) return null

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2 bg-blue-50 border-b border-blue-100">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-600">
          <Bell className="h-3 w-3" />
          <span>{t('reminders.upcoming')}</span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto max-h-[400px] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
        <div className="px-3 py-2 bg-blue-50">
          <Accordion type="single" collapsible className="space-y-1">
            {remindersData.reminders.map((reminder) => (
              <AccordionItem 
                key={reminder.id} 
                value={reminder.id.toString()}
                className={cn(
                  "border rounded-md overflow-hidden",
                  getDueStatusColor(reminder.next_trigger_at),
                  "shadow-sm hover:shadow-md transition-shadow duration-200"
                )}
              >
                <AccordionTrigger className="px-2 py-1.5 hover:no-underline">
                  <div className="flex flex-col items-start text-left">
                    <div className="font-medium text-xs">
                      {reminder.assets?.asset_name || reminder.assets?.name}
                    </div>
                    <div className="text-[10px] text-gray-500 flex flex-col">
                      <div className="flex items-center gap-1">
                        <span>{format(new Date(reminder.next_trigger_at), 'MMM d, yyyy', { locale: dateLocale })}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-400">
                          {formatDistanceToNow(new Date(reminder.next_trigger_at), { 
                            addSuffix: true,
                            locale: dateLocale 
                          })}
                        </span>
                      </div>
                      {(reminder.assets?.company || reminder.assets?.place) && (
                        <span className="text-gray-400">
                          {reminder.assets?.company?.name}
                          {reminder.assets?.company && reminder.assets?.place && " • "}
                          {reminder.assets?.place?.name}
                        </span>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-2 pb-2 space-y-2">
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <h4 className="font-medium text-xs">{reminder.title}</h4>
                      {reminder.description && (
                        <p className="text-xs text-gray-600">{reminder.description}</p>
                      )}
                    </div>

                    {/* Asset Information */}
                    <div className="space-y-1.5 border-l-2 border-apple-light-gray/30 pl-3">
                      <div className="text-xs uppercase tracking-wider text-apple-gray mb-1">{t('records.assetDetails')}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-apple-blue">
                          {reminder.assets?.asset_name || reminder.assets?.name}
                        </span>
                      </div>
                      {reminder.assets?.company && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-apple-gray">
                            {t('records.company')}: {reminder.assets.company.name}
                          </span>
                        </div>
                      )}
                      {reminder.assets?.place && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-apple-gray/80">
                            {t('records.place')}: {reminder.assets.place.name}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Status and Priority */}
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="outline" className="text-[10px]">
                        {reminder.onetime ? t('reminders.onetime') : `${t('reminders.every')} ${reminder.recurrency_value} ${reminder.recurrency_unit}`}
                      </Badge>
                      {reminder.priority && (
                        <Badge variant="outline" className={cn("text-[10px]", getPriorityColor(reminder.priority))}>
                          {reminder.priority}
                        </Badge>
                      )}
                      {reminder.status && (
                        <Badge variant="outline" className={cn("text-[10px]", getStatusColor(reminder.status))}>
                          {reminder.status}
                        </Badge>
                      )}
                    </div>

                    {/* Next Trigger Date */}
                    <div className="flex items-center gap-2 text-xs text-apple-gray">
                      <Calendar className="h-3 w-3" />
                      <span>{t('reminders.nextTrigger')}:</span>
                      <span className="text-apple-dark">
                        {format(new Date(reminder.next_trigger_at), 'MMM d, yyyy', { locale: dateLocale })}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-2 space-y-3">
                      <AppleButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCompleteClick(reminder)}
                        className="w-full text-xs py-1.5"
                      >
                        {t('reminders.complete')}
                      </AppleButton>
                      <Link to={`/assets/${reminder.assets.id}`} className="block">
                        <AppleButton
                          variant="secondary"
                          size="sm"
                          className="w-full text-xs py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600"
                        >
                          {t('reminders.goToAsset')}
                        </AppleButton>
                      </Link>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {/* Complete Reminder Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{t('records.completeReminder')}</DialogTitle>
              <DialogDescription>
                {t('records.completeReminderDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              <AssetRecordForm
                assetId={selectedReminder?.assets?.id}
                mode="create"
                onSuccess={handleCompleteSuccess}
                onCancel={() => setShowCompleteDialog(false)}
                initialData={{
                  title: selectedReminder?.title,
                  details: selectedReminder?.description,
                  type: "Maintenance",
                  status: "Completed",
                  priority: selectedReminder?.priority,
                  asset_id: selectedReminder?.assets?.id,
                  place_id: selectedReminder?.assets?.place?.id,
                  company_id: selectedReminder?.assets?.company?.id
                }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
} 
